/*
 * queue.js — the Capture-class guarantee, made real.
 *
 * A check-in NEVER waits on the network to show success:
 *   1. The capture is persisted to IndexedDB first (source of truth on-device).
 *   2. The UI is told "saved" immediately.
 *   3. A background flush attempts delivery through the adapter; retries on
 *      reconnect / interval. Duplicate delivery is prevented by a client
 *      capture id (the server/backend can also dedupe via CP-001's duplicate
 *      handling addendum later).
 *
 * syncStatus in the confirmation response:
 *   'saved'  — delivered to the backend
 *   'queued' — safe on this device, will sync automatically
 */
const DB_NAME = 'flow-capture'
const STORE = 'outbox'
const DB_VERSION = 1

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'captureId' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function tx(mode, fn) {
  const db = await openDb()
  try {
    return await new Promise((resolve, reject) => {
      const t = db.transaction(STORE, mode)
      const store = t.objectStore(STORE)
      const out = fn(store)
      t.oncomplete = () => resolve(out?.result ?? out)
      t.onerror = () => reject(t.error)
      t.onabort = () => reject(t.error)
    })
  } finally {
    db.close()
  }
}

function newCaptureId() {
  return globalThis.crypto?.randomUUID?.() ?? `cap-${Date.now()}-${performance.now()}`
}

export function createCaptureQueue(adapter, { onChange, onCheckinSynced } = {}) {
  let flushing = false

  async function pendingCount() {
    return tx('readonly', (store) => store.count())
  }

  async function listPending() {
    return tx('readonly', (store) => store.getAll())
  }

  // One delivery function per capture kind — all Capture-class, all optional
  // to sync now, none allowed to block.
  const deliver = {
    checkin: (item) => adapter.submitCheckIn(item.session, item.capturedAt),
    photo: (item) => adapter.submitPhotoNote(item.session, item.capturedAt, item.fileName),
    issue: (item) => adapter.submitIssue(item.session, item.capturedAt, item.text),
    // FD-001 lifecycle ops. A check-out/day-close for a check-in that hasn't
    // synced yet has no recordId — it throws and stays queued; the flush loop
    // fills the recordId in as soon as its check-in delivers.
    checkout: (item) => {
      if (!item.entry?.recordId && !item.entry?.held) throw new Error('waiting-for-checkin-sync')
      return adapter.submitCheckOut(item.session, item.entry, item.capturedAt)
    },
    dayclose: (item) => {
      if (!item.entry?.recordId) throw new Error('waiting-for-checkin-sync')
      return adapter.submitDayClose(item.session, item.entry, item.capturedAt)
    },
  }

  /** Fill a synced check-in's recordId into its dependent queued items. */
  async function resolveDependents(checkinCaptureId, recordId) {
    if (!recordId) return
    const items = await listPending().catch(() => [])
    for (const it of items) {
      if (it.dependsOn === checkinCaptureId && it.entry && !it.entry.recordId) {
        it.entry.recordId = recordId
        await tx('readwrite', (store) => store.put(it)).catch(() => {})
      }
    }
    onCheckinSynced?.(checkinCaptureId, recordId)
  }

  /**
   * Capture anything. Persist-first, then attempt delivery with a short
   * budget; if delivery can't complete quickly (offline, slow network, backend
   * down), the capture stays queued and the user still gets instant success.
   */
  async function capture(kind, session, capturedAtISO, extra = {}) {
    const captureId = newCaptureId()
    const item = {
      captureId,
      seq: Date.now(), // flush order: check-ins deliver before their check-outs
      kind,
      session: {
        tenant: session.tenant,
        employee: session.employee,
        project: session.project,
        confidence: session.confidence,
        assignmentId: session.assignmentId,
        projectCorrected: session.projectCorrected,
        gps: session.gps ?? null,
      },
      capturedAt: capturedAtISO,
      attempts: 0,
      ...extra,
    }

    // 1) Device-first persistence. If even IndexedDB fails, fall through to a
    //    direct network attempt rather than losing the capture.
    let persisted = true
    try {
      await tx('readwrite', (store) => store.put(item))
    } catch {
      persisted = false
    }
    notify()

    // 2) Fast delivery attempt (bounded). Success → remove from outbox.
    try {
      const result = await withTimeout(deliver[kind](item), 4000)
      if (persisted) await remove(captureId).catch(() => {})
      if (kind === 'checkin') await resolveDependents(captureId, result.recordId).catch(() => {})
      notify()
      // A duplicate or voided-disposition rejection is a completed delivery
      // (outbox item clears) but NOT a saved capture — preserve it so the UI
      // tells the user plainly instead of showing success.
      return { ...result, captureId, syncStatus: (result.duplicate || result.voided) ? 'rejected' : 'saved' }
    } catch {
      if (!persisted) {
        // Extremely unlikely double-failure: report honestly.
        return { success: false, recordId: null, captureId, checkedInAt: capturedAtISO, syncStatus: 'failed' }
      }
      return {
        success: true,
        recordId: `local:${captureId}`,
        captureId,
        checkedInAt: capturedAtISO,
        syncStatus: 'queued',
      }
    }
  }

  async function remove(captureId) {
    return tx('readwrite', (store) => store.delete(captureId))
  }

  /** Try to deliver everything in the outbox. Safe to call repeatedly. */
  async function flush() {
    if (flushing) return
    flushing = true
    try {
      const items = (await listPending()).sort((a, b) => (a.seq || 0) - (b.seq || 0))
      for (const item of items) {
        try {
          const result = await deliver[item.kind]?.(item)
          await remove(item.captureId)
          if (item.kind === 'checkin') await resolveDependents(item.captureId, result?.recordId).catch(() => {})
        } catch {
          item.attempts = (item.attempts || 0) + 1
          await tx('readwrite', (store) => store.put(item)).catch(() => {})
        }
      }
    } finally {
      flushing = false
      notify()
    }
  }

  function notify() {
    if (!onChange) return
    pendingCount().then((n) => onChange(n)).catch(() => {})
  }

  // Background sync triggers: reconnect + gentle interval.
  window.addEventListener('online', flush)
  const interval = setInterval(() => {
    if (navigator.onLine) flush()
  }, 60_000)

  return {
    captureCheckIn: (session, iso) => capture('checkin', session, iso),
    capturePhoto: (session, iso, fileName) => capture('photo', session, iso, { fileName }),
    captureIssue: (session, iso, text) => capture('issue', session, iso, { text }),
    // FD-001: entry = { recordId|null, checkedInAt, held }; dependsOn = the
    // check-in's captureId (used to backfill recordId when it syncs).
    captureCheckOut: (session, iso, entry, dependsOn) =>
      capture('checkout', session, iso, { entry, dependsOn }),
    captureDayClose: (session, iso, entry, dependsOn) =>
      capture('dayclose', session, iso, { entry, dependsOn }),
    flush,
    pendingCount,
    dispose() {
      window.removeEventListener('online', flush)
      clearInterval(interval)
    },
  }
}

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms)
    promise.then((v) => { clearTimeout(t); resolve(v) },
                 (e) => { clearTimeout(t); reject(e) })
  })
}
